// src/window.ts
var DEFAULT_WINDOW_DAYS = 14;
function parseTs(s) {
  if (s === null || s === void 0 || s === "") return null;
  const t = typeof s === "number" ? s : Date.parse(s);
  return Number.isFinite(t) ? t : null;
}
function isRatingWindowOpen(input = {}) {
  const now = input.now ?? /* @__PURE__ */ new Date();
  const rawOpen = input.openAt ?? null;
  const rawClose = input.closeAt ?? null;
  const open = parseTs(rawOpen);
  const close = parseTs(rawClose);
  const openAbsent = rawOpen === null || rawOpen === void 0 || rawOpen === "";
  const closeAbsent = rawClose === null || rawClose === void 0 || rawClose === "";
  if (openAbsent && closeAbsent) return true;
  if (openAbsent ? false : open === null) return false;
  if (closeAbsent ? false : close === null) return false;
  if (open !== null && now.getTime() < open) return false;
  if (close !== null && now.getTime() > close) return false;
  return true;
}
function formatRatingWindow(input = {}) {
  const now = input.now ?? /* @__PURE__ */ new Date();
  const rawOpen = input.openAt ?? null;
  const rawClose = input.closeAt ?? null;
  const open = parseTs(rawOpen);
  const close = parseTs(rawClose);
  const openAbsent = rawOpen === null || rawOpen === void 0 || rawOpen === "";
  const closeAbsent = rawClose === null || rawClose === void 0 || rawClose === "";
  if (openAbsent && closeAbsent) {
    return { isOpen: false, daysLeft: null, label: "No active rating window" };
  }
  if (openAbsent ? false : open === null) {
    return { isOpen: false, daysLeft: 0, label: "Closed" };
  }
  if (closeAbsent ? false : close === null) {
    return { isOpen: false, daysLeft: 0, label: "Closed" };
  }
  if (close !== null && now.getTime() > close) {
    return { isOpen: false, daysLeft: 0, label: "Closed" };
  }
  if (open !== null && now.getTime() < open) {
    const daysLeft2 = close !== null ? Math.max(0, Math.ceil((close - now.getTime()) / 864e5)) : DEFAULT_WINDOW_DAYS;
    return { isOpen: false, daysLeft: daysLeft2, label: `Opens in ${daysLeft2} day${daysLeft2 === 1 ? "" : "s"}` };
  }
  const daysLeft = close !== null ? Math.max(0, Math.ceil((close - now.getTime()) / 864e5)) : DEFAULT_WINDOW_DAYS;
  return {
    isOpen: true,
    daysLeft,
    label: `${daysLeft}-day window`
  };
}

// src/display.ts
var DEFAULT_MIN_CRITIC_THRESHOLD = 3;
function formatThresholdProgress(count, min) {
  if (count <= 0) {
    return {
      kind: "empty",
      label: "No ratings yet. Be the first to rate after watching."
    };
  }
  if (count < min) {
    return {
      kind: "progress",
      label: `${count} of ${min} ratings so far. Headline HonestScore appears at ${min}.`
    };
  }
  return { kind: "ready", label: "" };
}

// src/dimensions.ts
var SCORE_MIN = 1;
var SCORE_MAX = 5;
var RATING_DIMENSIONS = [
  {
    key: "problemSolved",
    label: "Problem Solved?",
    description: "How well did it address a real pain I have?"
  },
  {
    key: "proofBelievable",
    label: "Proof / Believable?",
    description: "Demos, evidence, testimonials \u2014 did it feel real and trustworthy?"
  },
  {
    key: "offerClear",
    label: "Offer Clear & Worth It?",
    description: "Value, pricing, bonuses, guarantee \u2014 would I bite?"
  },
  {
    key: "convincingBuy",
    label: "Convincing / Want to Buy?",
    description: "Persuasiveness and call-to-action strength."
  },
  {
    key: "overall",
    label: "Overall Recommendation",
    description: "Would you recommend or watch again / buy from this pitcher?"
  }
];
var ALLOWED_KEYS = new Set(RATING_DIMENSIONS.map((d) => d.key));
function isPlainObject(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function isIntInRange(n) {
  return typeof n === "number" && Number.isInteger(n) && n >= SCORE_MIN && n <= SCORE_MAX;
}
function validateRatingScores(input) {
  if (!isPlainObject(input)) {
    return { ok: false, reason: "rating must be an object" };
  }
  for (const dim of RATING_DIMENSIONS) {
    const v = input[dim.key];
    if (!isIntInRange(v)) {
      return { ok: false, reason: `${dim.key} must be an integer ${SCORE_MIN}..${SCORE_MAX}` };
    }
  }
  for (const k of Object.keys(input)) {
    if (!ALLOWED_KEYS.has(k)) {
      return { ok: false, reason: `unknown dimension "${k}"` };
    }
  }
  return { ok: true };
}
function round1(x) {
  return Number(x.toFixed(1));
}
function averageScore(scores) {
  let sum = 0;
  for (const dim of RATING_DIMENSIONS) {
    sum += scores[dim.key];
  }
  return round1(sum / RATING_DIMENSIONS.length);
}
function honestScoreFromRatings(ratings) {
  let sum = 0;
  let count = 0;
  for (const r of ratings) {
    if (validateRatingScores(r.scores).ok !== true) continue;
    sum += averageScore(r.scores);
    count += 1;
  }
  if (count === 0) return null;
  const out = round1(sum / count);
  if (out < SCORE_MIN) return SCORE_MIN;
  if (out > SCORE_MAX) return SCORE_MAX;
  return out;
}
function buildBreakdown(ratings) {
  const sums = /* @__PURE__ */ new Map();
  const counts = /* @__PURE__ */ new Map();
  for (const dim of RATING_DIMENSIONS) {
    sums.set(dim.key, 0);
    counts.set(dim.key, 0);
  }
  for (const r of ratings) {
    if (validateRatingScores(r.scores).ok !== true) continue;
    for (const dim of RATING_DIMENSIONS) {
      sums.set(dim.key, (sums.get(dim.key) ?? 0) + r.scores[dim.key]);
      counts.set(dim.key, (counts.get(dim.key) ?? 0) + 1);
    }
  }
  return RATING_DIMENSIONS.map((dim) => {
    const sum = sums.get(dim.key) ?? 0;
    const count = counts.get(dim.key) ?? 0;
    return {
      key: dim.key,
      label: dim.label,
      average: count > 0 ? round1(sum / count) : null,
      count
    };
  });
}
function enrichRatingSection(doc) {
  const d = doc && typeof doc === "object" ? doc : {};
  const ratings = Array.isArray(d.ratings) ? d.ratings : [];
  const honestScore = honestScoreFromRatings(ratings);
  const scoreBreakdown = buildBreakdown(ratings);
  const criticCount = scoreBreakdown.length > 0 && scoreBreakdown[0].count > 0 ? ratings.filter(
    (r) => validateRatingScores(r.scores).ok
  ).length : 0;
  const openAt = typeof d.openAt === "string" ? d.openAt : null;
  const closeAt = typeof d.closeAt === "string" ? d.closeAt : null;
  const windowOpen = isRatingWindowOpen({ openAt, closeAt });
  const windowDisplay = formatRatingWindow({ openAt, closeAt });
  const minCritics = DEFAULT_MIN_CRITIC_THRESHOLD;
  const threshold = formatThresholdProgress(criticCount, minCritics);
  const thresholdLabel = threshold.kind === "ready" ? null : threshold.label;
  return {
    honestScore,
    scoreBreakdown,
    criticCount,
    ratingWindowOpenAt: openAt,
    ratingWindowCloseAt: closeAt,
    // 0 when the window is unset (matches the "no window = open by
    // default" semantics of `isRatingWindowOpen`).
    ratingWindowDaysLeft: windowDisplay.daysLeft ?? 0,
    // "Should we offer a vote button?" — unset = open, so this is
    // almost always `true` on a fresh doc.
    ratingWindowIsOpen: windowOpen,
    // "What's the stats-line copy?" — unset = "No active rating
    // window", 14 days = "14-day window", past = "Closed".
    ratingWindowLabel: windowDisplay.label,
    // Back-compat alias for code paths that read the boolean by
    // the other name (e.g. `pitches.js` exposed `ratingWindowOpen`
    // in the previous version of the enrichment).
    ratingWindowOpen: windowOpen,
    // Phase 5: the below-threshold progress copy. `null` when
    // the threshold is met (the "ready" branch in
    // `formatThresholdProgress`).
    thresholdLabel
  };
}
function enrichRatingSectionFromCache(doc, cacheRow) {
  const d = doc && typeof doc === "object" ? doc : {};
  const { honestScore, criticCount } = readCachedAggregateFromCacheRow(
    cacheRow ?? {}
  );
  const openAt = typeof d.openAt === "string" ? d.openAt : null;
  const closeAt = typeof d.closeAt === "string" ? d.closeAt : null;
  const windowOpen = isRatingWindowOpen({ openAt, closeAt });
  const windowDisplay = formatRatingWindow({ openAt, closeAt });
  const threshold = formatThresholdProgress(criticCount, DEFAULT_MIN_CRITIC_THRESHOLD);
  const thresholdLabel = threshold.kind === "ready" ? null : threshold.label;
  return {
    honestScore,
    // 2026-06-29: empty breakdown is the cache path's honest
    // signal — the cache does NOT carry per-dimension values, so
    // pretending we have them would mislead the template. A
    // template that needs the breakdown calls the live enrich.
    scoreBreakdown: [],
    criticCount,
    ratingWindowOpenAt: openAt,
    ratingWindowCloseAt: closeAt,
    ratingWindowDaysLeft: windowDisplay.daysLeft ?? 0,
    ratingWindowIsOpen: windowOpen,
    ratingWindowLabel: windowDisplay.label,
    ratingWindowOpen: windowOpen,
    thresholdLabel
  };
}

// src/aggregate.ts
var RATING_DIM_KEYS = [
  "problemSolved",
  "proofBelievable",
  "offerClear",
  "convincingBuy",
  "overall"
];
function parseScoresField(raw) {
  if (raw == null) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const v = JSON.parse(raw);
      if (v && typeof v === "object" && !Array.isArray(v)) {
        return v;
      }
    } catch {
    }
  }
  return null;
}
function perRatingMean(scores) {
  let sum = 0;
  for (const k of RATING_DIM_KEYS) sum += scores[k];
  return Math.round(sum / RATING_DIM_KEYS.length * 10) / 10;
}
function aggregateFromRatings(rows, cap = 500) {
  let sum = 0;
  let count = 0;
  for (const r of rows.slice(0, cap)) {
    const parsed = parseScoresField(r.scores);
    if (!parsed) continue;
    if (validateRatingScores(parsed).ok !== true) continue;
    sum += perRatingMean(parsed);
    count += 1;
  }
  if (count === 0) return { honestScore: null, criticCount: 0 };
  let mean = Math.round(sum / count * 10) / 10;
  if (mean < SCORE_MIN) mean = SCORE_MIN;
  if (mean > SCORE_MAX) mean = SCORE_MAX;
  return { honestScore: mean, criticCount: count };
}
function readCachedAggregateFromCacheRow(row) {
  let score = null;
  const raw = row.honestScore;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    score = raw;
    if (score < SCORE_MIN) score = SCORE_MIN;
    if (score > SCORE_MAX) score = SCORE_MAX;
  }
  const rawCount = row.criticCount;
  const count = typeof rawCount === "number" && Number.isFinite(rawCount) ? Math.max(0, Math.trunc(rawCount)) : 0;
  return { honestScore: score, criticCount: count };
}

// src/scale.ts
var SCORE_EMOJI = ["\u{1F621}", "\u{1F615}", "\u{1F610}", "\u{1F642}", "\u{1F603}"];
var SCORE_LABEL = ["Terrible", "Meh", "Okay", "Good", "Excellent"];
function scoreLevelFromValue(v) {
  return v - 1;
}
export {
  DEFAULT_MIN_CRITIC_THRESHOLD,
  RATING_DIMENSIONS,
  SCORE_EMOJI,
  SCORE_LABEL,
  SCORE_MAX,
  SCORE_MIN,
  aggregateFromRatings,
  averageScore,
  buildBreakdown,
  enrichRatingSection,
  enrichRatingSectionFromCache,
  formatRatingWindow,
  formatThresholdProgress,
  honestScoreFromRatings,
  isRatingWindowOpen,
  readCachedAggregateFromCacheRow,
  scoreLevelFromValue,
  validateRatingScores
};
//# sourceMappingURL=index.js.map